import React, { useState } from 'react';
import PlanEditor from './PlanEditor';
import AiCoach from './AiCoach';
import { errorText, formatTarget, toPlanRequest } from './strengthFormat';

function PlansView({ api, t, lang, plans, catalog, catalogByKey, showMessage, onPlansChanged, onTrainStarted }) {
  const [mode, setMode] = useState('list');
  const [editing, setEditing] = useState(null);
  const [owner, setOwner] = useState('all');
  const [openRationale, setOpenRationale] = useState({});
  const [saving, setSaving] = useState(false);

  const owners = [...new Map(plans.map(p => [p.userId, { userId: p.userId, name: p.isMine ? t('strength.common.me') : p.ownerName }])).values()];
  const filtered = plans.filter(p => owner === 'all' || p.userId === owner);

  const save = async (request) => {
    setSaving(true);
    try {
      if (editing?.id) await api.updatePlan(editing.id, request);
      else await api.createPlan(request);
      await onPlansChanged();
      showMessage(t('strength.plans.saved'));
      setMode('list');
      setEditing(null);
    } catch (e) {
      showMessage(errorText(e, t('strength.errors.save')), 'error');
    } finally {
      setSaving(false);
    }
  };

  const startDay = async (plan, day) => {
    try {
      await api.startSession({ planId: plan.id, planDayId: day.id });
    } catch (e) {
      showMessage(errorText(e, t('strength.errors.start')), 'error');
    }
    onTrainStarted();
  };

  const copy = async (plan) => {
    try {
      await api.copyPlan(plan.id);
      await onPlansChanged();
      showMessage(t('strength.plans.copied', { name: plan.name }));
    } catch (e) {
      showMessage(errorText(e, t('strength.errors.save')), 'error');
    }
  };

  const remove = async (plan) => {
    if (!window.confirm(t('strength.plans.deleteConfirm', { name: plan.name }))) return;
    try {
      await api.deletePlan(plan.id);
      await onPlansChanged();
    } catch (e) {
      showMessage(errorText(e, t('strength.errors.save')), 'error');
    }
  };

  if (mode === 'edit') {
    return (
      <PlanEditor
        t={t}
        lang={lang}
        initial={editing.request}
        isNew={!editing.id}
        catalog={catalog}
        catalogByKey={catalogByKey}
        saving={saving}
        onSave={save}
        onCancel={() => { setMode('list'); setEditing(null); }}
      />
    );
  }

  if (mode === 'ai') {
    return (
      <AiCoach
        api={api}
        t={t}
        lang={lang}
        catalogByKey={catalogByKey}
        showMessage={showMessage}
        onSaved={async () => { await onPlansChanged(); setMode('list'); }}
        onEdit={(draftPlan) => { setEditing({ id: null, request: toPlanRequest(draftPlan) }); setMode('edit'); }}
        onCancel={() => setMode('list')}
      />
    );
  }

  return (
    <div className="st-plans">
      <div className="st-toolbar">
        <div className="st-chips" role="group" aria-label={t('strength.plans.filter')}>
          <button type="button" className={`st-chip ${owner === 'all' ? 'active' : ''}`} onClick={() => setOwner('all')}>
            {t('strength.common.everyone')}
          </button>
          {owners.map(o => (
            <button type="button" key={o.userId} className={`st-chip ${owner === o.userId ? 'active' : ''}`} onClick={() => setOwner(o.userId)}>
              {o.name}
            </button>
          ))}
        </div>
        <div className="st-row">
          <button type="button" className="st-btn st-btn-ai" onClick={() => setMode('ai')}>✨ {t('strength.plans.aiCoach')}</button>
          <button
            type="button"
            className="st-btn"
            onClick={() => {
              setEditing({ id: null, request: { name: '', goal: 'GeneralFitness', daysPerWeek: 3, days: [{ name: t('strength.editor.dayName', { n: 1 }), exercises: [] }] } });
              setMode('edit');
            }}
          >
            ＋ {t('strength.plans.newPlan')}
          </button>
        </div>
      </div>

      {filtered.length === 0 && (
        <div className="st-empty">
          <p>{t('strength.plans.empty')}</p>
          <button type="button" className="st-btn st-btn-ai" onClick={() => setMode('ai')}>✨ {t('strength.plans.aiCoach')}</button>
        </div>
      )}

      <div className="st-plan-list">
        {filtered.map(plan => (
          <article key={plan.id} className="st-card st-plan">
            <header className="st-card-head">
              <div>
                <h3>
                  {plan.name}
                  {plan.isAiGenerated && <span className="st-badge st-badge-ai">✨ AI</span>}
                </h3>
                <p className="st-muted">
                  {!plan.isMine && <span className="st-badge">{plan.ownerName}</span>}
                  {t(`strength.goals.${plan.goal}`)} · {t('strength.plans.daysPerWeek', { count: plan.daysPerWeek })}
                </p>
              </div>
              <div className="st-row">
                {plan.isMine ? (
                  <>
                    <button type="button" className="st-btn st-btn-small" onClick={() => { setEditing({ id: plan.id, request: toPlanRequest(plan) }); setMode('edit'); }}>
                      {t('strength.common.edit')}
                    </button>
                    <button type="button" className="st-btn st-btn-small st-btn-danger-ghost" onClick={() => remove(plan)}>
                      {t('strength.common.delete')}
                    </button>
                  </>
                ) : (
                  <button type="button" className="st-btn st-btn-small" onClick={() => copy(plan)}>
                    {t('strength.plans.copy')}
                  </button>
                )}
              </div>
            </header>

            {plan.description && <p>{plan.description}</p>}
            {plan.aiRationale && (
              <div className="st-rationale">
                <button
                  type="button"
                  className="st-link-btn"
                  aria-expanded={!!openRationale[plan.id]}
                  onClick={() => setOpenRationale(prev => ({ ...prev, [plan.id]: !prev[plan.id] }))}
                >
                  {t('strength.plans.why')} {openRationale[plan.id] ? '▴' : '▾'}
                </button>
                {openRationale[plan.id] && <p>{plan.aiRationale}</p>}
              </div>
            )}

            <div className="st-days">
              {plan.days.map(day => (
                <div key={day.id} className={`st-day ${plan.nextDayId === day.id ? 'next' : ''}`}>
                  <div className="st-day-head">
                    <h4>
                      {day.name}
                      {plan.nextDayId === day.id && <span className="st-badge st-badge-next">{t('strength.train.upNext')}</span>}
                    </h4>
                    <button type="button" className="st-btn st-btn-small st-btn-primary" onClick={() => startDay(plan, day)}>
                      ▶ {t('strength.train.start')}
                    </button>
                  </div>
                  <ul className="st-day-exercises">
                    {day.exercises.map(e => (
                      <li key={e.id}>
                        <span>{e.exerciseName}</span>
                        <span className="st-muted">{formatTarget(e, lang)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}

export default PlansView;
